import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";

const TOOLTIP_GAP = 8;
const TOOLTIP_MAX_WIDTH = 420;

/**
 * Small info icon that shows a tooltip on hover. Used next to labels that have explanations.
 */
function InfoWithTooltip({ text }) {
  const triggerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placeAbove: true });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const placeAbove = rect.top > 120;
    setPosition({
      top: placeAbove ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
      left: rect.left + rect.width / 2,
      placeAbove,
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible || !triggerRef.current) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [visible, updatePosition]);

  if (!text || !text.trim()) return null;

  const tooltipEl =
    visible && typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <div
            className="ga-tooltip ga-tooltip--portal"
            role="tooltip"
            style={{
              position: "fixed",
              left: position.left,
              top: position.top,
              transform: position.placeAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
              maxWidth: TOOLTIP_MAX_WIDTH,
              zIndex: 999999,
              padding: "8px 12px",
              background: "#1f2937",
              color: "#f9fafb",
              fontSize: "13px",
              lineHeight: 1.4,
              borderRadius: "6px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              whiteSpace: "pre-wrap",
            }}
          >
            {text}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span
        ref={triggerRef}
        className="ga-info-icon"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="Explanation"
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: "4px",
          cursor: "help",
          color: "#6b7280",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path
            d="M8 7v4M8 5.5a1 1 0 100-2 1 1 0 000 2z"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </span>
      {tooltipEl}
    </>
  );
}

export default InfoWithTooltip;
